import React, { useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Button, Table, Alert, Spinner, Form, InputGroup, Card } from 'react-bootstrap'
import { useAdminPermissions } from 'api/hooks/use-admin-permissions'
import AppBadge from 'shared/badges/app-badge'
import SelectField from 'shared/forms/SelectField'
import TableFooter from 'shared/tables/table-footer'

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
]

const permissionHasExpansiveValue = (perm) =>
  (perm.values || []).some((value) => value.is_expansive)

const matchesYesNo = (filterValue, actual) => {
  if (!filterValue) return true
  return filterValue === 'yes' ? !!actual : !actual
}

// Sentinel used to filter permissions that aren't mapped to a variable.
const UNMAPPED = '__unmapped__'

const PermissionsList = () => {
  const { data: permissions, isLoading, error } = useAdminPermissions()
  const [params, setParams] = useState({
    search: '',
    categoryCaption: '',
    permissionVariable: '',
    highRisk: '',
    superuser: '',
    nonExpansive: '',
    page: 1,
    perPage: 25
  })

  // Filter changes should always send the user back to the first page so the
  // current page stays in range with the new (smaller) result set.
  const setFilter = useCallback((newParams) => {
    setParams({ ...newParams, page: 1 })
  }, [])

  const categoryCaptionOptions = useMemo(() => {
    if (!permissions) return []
    const captions = new Set()
    permissions.forEach((perm) => {
      if (perm.category_caption) captions.add(perm.category_caption)
    })
    return Array.from(captions)
      .sort((a, b) => a.localeCompare(b))
      .map((caption) => ({ value: caption, label: caption }))
  }, [permissions])

  const permissionVariableOptions = useMemo(() => {
    if (!permissions) return []
    const names = new Set()
    let hasUnmapped = false
    permissions.forEach((perm) => {
      if (perm.permission_variable_name) names.add(perm.permission_variable_name)
      else hasUnmapped = true
    })
    const options = Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }))
    if (hasUnmapped) options.push({ value: UNMAPPED, label: 'Unmapped' })
    return options
  }, [permissions])

  const filteredPermissions = useMemo(() => {
    if (!permissions) return []
    const term = params.search.toLowerCase()
    return permissions.filter((perm) => {
      const matchesSearch =
        !term ||
        perm.name?.toLowerCase().includes(term) ||
        perm.description?.toLowerCase().includes(term) ||
        perm.category?.toLowerCase().includes(term) ||
        perm.category_caption?.toLowerCase().includes(term)

      const matchesCaption =
        !params.categoryCaption || perm.category_caption === params.categoryCaption

      const matchesVariable =
        !params.permissionVariable ||
        (params.permissionVariable === UNMAPPED
          ? !perm.permission_variable_name
          : perm.permission_variable_name === params.permissionVariable)

      return (
        matchesSearch &&
        matchesCaption &&
        matchesVariable &&
        matchesYesNo(params.highRisk, perm.is_high_risk) &&
        matchesYesNo(params.superuser, perm.is_superuser) &&
        matchesYesNo(params.nonExpansive, !permissionHasExpansiveValue(perm))
      )
    })
  }, [permissions, params])

  const meta = useMemo(() => {
    const total = filteredPermissions.length
    return {
      total,
      pages: Math.max(1, Math.ceil(total / params.perPage)),
      title: 'permissions'
    }
  }, [filteredPermissions.length, params.perPage])

  const pagedPermissions = useMemo(() => {
    const start = (params.page - 1) * params.perPage
    return filteredPermissions.slice(start, start + params.perPage)
  }, [filteredPermissions, params.page, params.perPage])

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Error loading permissions: {error.message}</Alert>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Permissions</h2>
        <Button as={Link} to="/admin/permissions/new" variant="primary">Create Permission</Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, description, category..."
              value={params.search}
              onChange={(e) => setFilter({ ...params, search: e.target.value })}
            />
          </InputGroup>

          <SelectField
            params={params}
            setParams={setFilter}
            options={categoryCaptionOptions}
            accessorKey="categoryCaption"
            title="Category Caption"
            placeholder="All category captions"
          />
          <SelectField
            params={params}
            setParams={setFilter}
            options={permissionVariableOptions}
            accessorKey="permissionVariable"
            title="Permission Variable"
            placeholder="All permission variables"
          />
          <SelectField
            params={params}
            setParams={setFilter}
            options={YES_NO_OPTIONS}
            accessorKey="highRisk"
            title="High Risk"
            placeholder="All"
          />
          <SelectField
            params={params}
            setParams={setFilter}
            options={YES_NO_OPTIONS}
            accessorKey="superuser"
            title="Superuser"
            placeholder="All"
          />
          <SelectField
            params={params}
            setParams={setFilter}
            options={YES_NO_OPTIONS}
            accessorKey="nonExpansive"
            title="No Expansive Value"
            placeholder="All"
          />
        </Card.Body>
      </Card>

      <div className="mb-3">
        <strong>Showing {filteredPermissions.length} of {permissions?.length || 0} permissions</strong>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>Category Caption</th>
            <th>Permission Variable</th>
            <th>High Risk</th>
            <th>Superuser</th>
            <th>No Expansive Value</th>
            <th>Values</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pagedPermissions.map((perm) => (
            <tr key={perm.id}>
              <td>{perm.name}</td>
              <td><code>{perm.category}</code></td>
              <td>{perm.category_caption || '-'}</td>
              <td>
                {perm.permission_variable_name
                  ? <code>{perm.permission_variable_name}</code>
                  : <span className="text-muted">Unmapped</span>}
              </td>
              <td>{perm.is_high_risk ? <AppBadge variant="danger">Yes</AppBadge> : <AppBadge variant="secondary">No</AppBadge>}</td>
              <td>{perm.is_superuser ? <AppBadge variant="warning">Yes</AppBadge> : <AppBadge variant="secondary">No</AppBadge>}</td>
              <td>{permissionHasExpansiveValue(perm) ? <AppBadge variant="secondary">No</AppBadge> : <AppBadge variant="warning">Yes</AppBadge>}</td>
              <td><AppBadge variant="primary">{perm.values_count}</AppBadge></td>
              <td>
                <Button as={Link} to={`/admin/permissions/${perm.id}`} variant="info" size="sm" className="me-2">View</Button>
                <Button as={Link} to={`/admin/permissions/${perm.id}/edit`} variant="warning" size="sm">Edit</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {filteredPermissions.length === 0 ? (
        <Alert variant="info">No permissions found.</Alert>
      ) : (
        <TableFooter meta={meta} params={params} setParams={setParams} />
      )}
    </div>
  )
}

export default PermissionsList
